// ======================================
// Admin Content Manager Module
// ======================================

import { apiRequest } from '../../lib/api.js';
import { renderAdminLayout, initAdminLayout } from './admin-layout.js';

export function renderBlogAdmin() {
  const content = `
      <div class="admin-page-header">
        <div>
          <h2 class="admin-page-title" style="margin:0;">Content Manager</h2>
          <p class="admin-page-subtitle">Manage blog posts, portfolio, and page content</p>
        </div>
        <button class="btn btn-primary" id="new-post-btn">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          New Post
        </button>
      </div>

      <!-- Tabs -->
      <div style="display:flex;gap:var(--space-2);margin-bottom:var(--space-6);border-bottom:1px solid var(--color-border);padding-bottom:var(--space-2);">
        <button class="btn btn-primary content-tab active" data-tab="blog">Blog Posts</button>
        <button class="btn btn-outline content-tab" data-tab="portfolio">Portfolio</button>
      </div>

      <!-- Blog Posts List -->
      <div id="blog-tab" class="tab-content">
        <div class="admin-card">
          <div id="posts-list" style="display:flex;flex-direction:column;gap:var(--space-4);">
            <p style="text-align:center;padding:var(--space-8);color:var(--color-text-muted);">Loading posts...</p>
          </div>
        </div>
      </div>

      <!-- Portfolio List -->
      <div id="portfolio-tab" class="tab-content" style="display:none;">
        <div class="admin-card">
          <div id="portfolio-list" style="display:flex;flex-direction:column;gap:var(--space-4);">
            <p style="text-align:center;padding:var(--space-8);color:var(--color-text-muted);">Loading portfolio...</p>
          </div>
        </div>
      </div>

      <!-- Post Editor Modal -->
      <div class="modal-overlay" id="post-modal" style="display:none;">
        <div class="modal-dialog" style="max-width:700px;">
          <div class="modal-header">
            <h3 id="post-modal-title">New Blog Post</h3>
            <button class="modal-close" id="post-modal-close">&times;</button>
          </div>
          <form id="post-form" class="modal-body" style="display:flex;flex-direction:column;gap:var(--space-4);">
            <input type="hidden" id="post-id" />
            <div class="form-group">
              <label class="form-label">Title</label>
              <input type="text" id="post-title" class="form-input" required placeholder="Post title..." />
            </div>
            <div style="display:grid;grid-template-columns:1fr 1fr;gap:var(--space-4);">
              <div class="form-group">
                <label class="form-label">Category</label>
                <select id="post-category" class="form-input">
                  <option value="general">General</option>
                  <option value="kitchen">Kitchen</option>
                  <option value="bedroom">Bedroom</option>
                  <option value="living_room">Living Room</option>
                  <option value="guides">Guides</option>
                  <option value="trends">Trends</option>
                  <option value="tips">Tips</option>
                </select>
              </div>
              <div class="form-group">
                <label class="form-label">Status</label>
                <select id="post-status" class="form-input">
                  <option value="draft">Draft</option>
                  <option value="published">Published</option>
                  <option value="archived">Archived</option>
                </select>
              </div>
            </div>
            <div class="form-group">
              <label class="form-label">Featured Image URL</label>
              <input type="text" id="post-image" class="form-input" placeholder="https://images.unsplash.com/..." />
            </div>
            <div class="form-group">
              <label class="form-label">Excerpt</label>
              <textarea id="post-excerpt" class="form-input" rows="2" placeholder="Brief summary..."></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Content (HTML)</label>
              <textarea id="post-content" class="form-input" rows="12" required placeholder="<h2>Your content...</h2><p>Write here...</p>" style="font-family:monospace;font-size:13px;"></textarea>
            </div>
            <div class="form-group">
              <label class="form-label">Tags (comma separated)</label>
              <input type="text" id="post-tags" class="form-input" placeholder="design, kitchen, modern" />
            </div>
            <button type="submit" class="btn btn-primary" style="width:100%;">Save Post</button>
          </form>
        </div>
      </div>
    `;

  return renderAdminLayout('content', 'Content Manager', content);
}

export function initBlogAdmin() {
  initAdminLayout();
  let activeTab = 'blog';

  async function loadPosts() {
    try {
      const data = await apiRequest('/blog?admin=1&limit=50');
      const posts = data.data?.posts || data.posts || [];
      const container = document.getElementById('posts-list');
      if (!posts.length) {
        container.innerHTML = '<p style="text-align:center;padding:var(--space-8);color:var(--color-text-muted);">No posts yet. Create your first post!</p>';
        return;
      }
      container.innerHTML = posts.map(p => `
        <div style="display:flex;align-items:center;gap:var(--space-4);padding:var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-lg);background:var(--color-surface);">
          ${p.featured_image ? `<img src="${p.featured_image}" alt="" style="width:80px;height:60px;object-fit:cover;border-radius:var(--radius-md);" />` : '<div style="width:80px;height:60px;background:var(--color-surface-warm);border-radius:var(--radius-md);display:flex;align-items:center;justify-content:center;color:var(--color-text-muted);font-size:12px;">No img</div>'}
          <div style="flex:1;min-width:0;">
            <h4 style="margin:0;font-size:15px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${p.title}</h4>
            <p style="margin:4px 0 0;font-size:12px;color:var(--color-text-muted);">${p.category} · ${p.views || 0} views · ${new Date(p.created_at).toLocaleDateString()}</p>
          </div>
          <span class="badge ${p.status === 'published' ? 'badge-success' : 'badge-secondary'}">${p.status}</span>
          <button class="btn btn-sm btn-outline edit-post" data-id="${p.id}">Edit</button>
          <button class="btn btn-sm btn-danger delete-post" data-id="${p.id}">Delete</button>
        </div>
      `).join('');

      container.querySelectorAll('.edit-post').forEach(btn => {
        btn.addEventListener('click', () => editPost(btn.dataset.id));
      });
      container.querySelectorAll('.delete-post').forEach(btn => {
        btn.addEventListener('click', async () => {
          if (confirm('Delete this post?')) {
            await apiRequest(`/blog/${btn.dataset.id}`, { method: 'DELETE' });
            loadPosts();
          }
        });
      });
    } catch (e) {
      document.getElementById('posts-list').innerHTML = '<p style="text-align:center;color:var(--color-error);">Failed to load posts</p>';
    }
  }

  async function loadPortfolio() {
    try {
      const data = await apiRequest('/portfolio?limit=50');
      const items = data.data || [];
      const container = document.getElementById('portfolio-list');
      if (!items.length) {
        container.innerHTML = '<p style="text-align:center;padding:var(--space-8);color:var(--color-text-muted);">No portfolio items yet.</p>';
        return;
      }
      container.innerHTML = items.map(item => `
        <div style="display:flex;align-items:center;gap:var(--space-4);padding:var(--space-4);border:1px solid var(--color-border);border-radius:var(--radius-lg);">
          ${item.featured_image ? `<img src="${item.featured_image}" alt="" style="width:80px;height:60px;object-fit:cover;border-radius:var(--radius-md);" />` : ''}
          <div style="flex:1;">
            <h4 style="margin:0;">${item.title}</h4>
            <p style="margin:4px 0 0;font-size:12px;color:var(--color-text-muted);">${item.category} · ${item.city || 'N/A'} · ${item.area_sqft || '?'} sq.ft</p>
          </div>
          <span class="badge badge-info">${item.budget_range || 'N/A'}</span>
        </div>
      `).join('');
    } catch (e) {
      document.getElementById('portfolio-list').innerHTML = '<p style="color:var(--color-error);">Failed to load portfolio</p>';
    }
  }

  async function editPost(id) {
    try {
      const data = await apiRequest(`/blog/${id}`);
      const post = data.data || data;
      openModal(post);
    } catch (e) { alert('Failed to load post'); }
  }

  function openModal(post = null) {
    document.getElementById('post-modal').style.display = 'flex';
    document.getElementById('post-modal-title').textContent = post ? 'Edit Post' : 'New Blog Post';
    document.getElementById('post-id').value = post ? post.id : '';
    document.getElementById('post-title').value = post ? post.title : '';
    document.getElementById('post-category').value = post ? post.category : 'general';
    document.getElementById('post-status').value = post ? post.status : 'draft';
    document.getElementById('post-image').value = post ? (post.featured_image || '') : '';
    document.getElementById('post-excerpt').value = post ? (post.excerpt || '') : '';
    document.getElementById('post-content').value = post ? (post.content || '') : '';
    const tags = post?.tags ? (typeof post.tags === 'string' ? JSON.parse(post.tags) : post.tags) : [];
    document.getElementById('post-tags').value = tags.join(', ');
  }

  // Tab switching
  document.querySelectorAll('.content-tab').forEach(tab => {
    tab.addEventListener('click', () => {
      document.querySelectorAll('.content-tab').forEach(t => { t.classList.remove('active'); t.classList.remove('btn-primary'); t.classList.add('btn-outline'); });
      tab.classList.add('active', 'btn-primary');
      tab.classList.remove('btn-outline');
      activeTab = tab.dataset.tab;
      document.getElementById('blog-tab').style.display = activeTab === 'blog' ? 'block' : 'none';
      document.getElementById('portfolio-tab').style.display = activeTab === 'portfolio' ? 'block' : 'none';
      document.getElementById('new-post-btn').style.display = activeTab === 'blog' ? '' : 'none';
      if (activeTab === 'portfolio') loadPortfolio();
    });
  });

  document.getElementById('new-post-btn')?.addEventListener('click', () => openModal());
  document.getElementById('post-modal-close')?.addEventListener('click', () => {
    document.getElementById('post-modal').style.display = 'none';
  });

  document.getElementById('post-form')?.addEventListener('submit', async (e) => {
    e.preventDefault();
    const id = document.getElementById('post-id').value;
    const tagsStr = document.getElementById('post-tags').value;
    const tags = tagsStr ? tagsStr.split(',').map(t => t.trim()).filter(Boolean) : [];
    const body = {
      title: document.getElementById('post-title').value,
      category: document.getElementById('post-category').value,
      status: document.getElementById('post-status').value,
      featured_image: document.getElementById('post-image').value || null,
      excerpt: document.getElementById('post-excerpt').value,
      content: document.getElementById('post-content').value,
      tags
    };

    try {
      if (id) {
        await apiRequest(`/blog/${id}`, { method: 'PUT', body });
      } else {
        await apiRequest('/blog', { method: 'POST', body });
      }
      document.getElementById('post-modal').style.display = 'none';
      loadPosts();
    } catch (e) { alert('Save failed: ' + e.message); }
  });

  loadPosts();
}
